import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setEnrollmentProduct } from '@/redux/actions/enrollment'

/**
 * Enroll Redirect Page
 *
 * This page handles incoming links from abandoned cart follow-up emails.
 * It extracts the product type and ID from the URL, sets them in Redux,
 * and redirects to the enrollment page.
 *
 * URL Format: /enroll/:productType/:productId
 * Example: /enroll/course/507f1f77bcf86cd799439011?utm_source=email&utm_campaign=abandoned_cart_24h
 */
const Enroll: React.FC = () => {
    const { productType, productId } = useParams<{ productType: string; productId: string }>()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        // Track UTM parameters if needed (for analytics)
        const utmSource = searchParams.get('utm_source')
        const utmCampaign = searchParams.get('utm_campaign')

        if (utmSource || utmCampaign) {
            // You could send this to analytics here if needed
        }

        // Validate product type
        if (!productType || (productType !== 'course' && productType !== 'bundle' && productType !== 'digital_product')) {
            setError('Invalid product type. Please try again.')
            setTimeout(() => navigate('/courses'), 3000)
            return
        }

        // Validate product ID
        if (!productId || productId.length < 10) {
            setError('Invalid product ID. Please try again.')
            setTimeout(() => navigate('/courses'), 3000)
            return
        }

        // Set the enrollment product in Redux
        dispatch(setEnrollmentProduct(productId, productType))

        // Navigate to the appropriate checkout page based on product type
        // Small delay to ensure Redux state is set
        setTimeout(() => {
            if (productType === 'digital_product') {
                navigate('/digital-product-checkout', { replace: true })
            } else {
                navigate('/enrollment', { replace: true })
            }
        }, 100)
    }, [productType, productId, dispatch, navigate, searchParams])

    // Loading state while redirecting
    if (!error) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    {/* Loading spinner */}
                    <div className="relative w-16 h-16 mx-auto mb-6">
                        <div className="absolute inset-0 border-4 border-purple-500/20 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin"></div>
                    </div>
                    <h2 className="text-xl font-semibold text-white mb-2">
                        Preparing Your Enrollment
                    </h2>
                    <p className="text-gray-400">
                        Redirecting you to complete your enrollment...
                    </p>
                </div>
            </div>
        )
    }

    // Error state
    return (
        <div className="min-h-screen bg-black flex items-center justify-center">
            <div className="text-center max-w-md mx-auto px-4">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                    <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <h2 className="text-xl font-semibold text-white mb-2">
                    Oops! Something went wrong
                </h2>
                <p className="text-gray-400 mb-6">
                    {error}
                </p>
                <p className="text-gray-500 text-sm">
                    Redirecting to courses page...
                </p>
            </div>
        </div>
    )
}

export default Enroll
